"use strict";
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
Object.defineProperty(exports, "__esModule", { value: true });
exports.fetchMCPTools = fetchMCPTools;
exports.readMCPToolsFromFile = readMCPToolsFromFile;
exports.probeMCPServerAuth = probeMCPServerAuth;
exports.resolveMCPOAuthMetadata = resolveMCPOAuthMetadata;
const tslib_1 = require("tslib");
const axios_1 = tslib_1.__importDefault(require("axios"));
const localizeUtils_1 = require("../../common/localizeUtils");
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
/**
 * Fetch MCP tool definitions from a remote MCP server using the MCP JSON-RPC protocol.
 * Uses StreamableHTTP transport via the @modelcontextprotocol/sdk.
 *
 * @param serverUrl - The URL of the remote MCP server
 * @returns MCPFetchResult with tools if no auth, or requiresAuth flag if auth needed
 */
async function fetchMCPTools(serverUrl) {
    // First, attempt auth detection by sending a HEAD/GET request
    let authMetadataUrl;
    try {
        await axios_1.default.get(serverUrl, { timeout: 10000 });
    }
    catch (error) {
        if (error?.response?.status === 401 || error?.status === 401) {
            // Auth required — try to extract OAuth metadata
            const wwwAuth = error?.response?.headers?.["www-authenticate"];
            if (wwwAuth) {
                const match = wwwAuth.match(/resource_metadata=\s*"([^"]+)"/);
                if (match) {
                    authMetadataUrl = match[1];
                }
            }
            return { requiresAuth: true, tools: [], authMetadataUrl };
        }
        // For non-401 errors, try fetching tools anyway via MCP protocol
    }
    // Try to fetch tools using MCP JSON-RPC protocol over StreamableHTTP.
    // Uses @modelcontextprotocol/sdk as a library dependency (not the ATK CLI itself).
    try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - dynamic import of MCP SDK subpath
        const { Client } = await Promise.resolve().then(() => tslib_1.__importStar(require("@modelcontextprotocol/sdk/client/index.js")));
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - dynamic import of MCP SDK subpath
        const { StreamableHTTPClientTransport } = await Promise.resolve().then(() => tslib_1.__importStar(require("@modelcontextprotocol/sdk/client/streamableHttp.js")));
        const transport = new StreamableHTTPClientTransport(new URL(serverUrl));
        const client = new Client({ name: "atk-cli", version: "1.0.0" });
        try {
            await client.connect(transport);
            const result = await client.listTools();
            const tools = result.tools.map((tool) => ({
                ...tool,
                description: tool.description ?? "",
            }));
            return { requiresAuth: false, tools };
        }
        finally {
            await client.close();
        }
    }
    catch (error) {
        // If MCP SDK connection fails, fall back to SSE transport
        try {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - dynamic import of MCP SDK subpath
            const { Client } = await Promise.resolve().then(() => tslib_1.__importStar(require("@modelcontextprotocol/sdk/client/index.js")));
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore - dynamic import of MCP SDK subpath
            const { SSEClientTransport } = await Promise.resolve().then(() => tslib_1.__importStar(require("@modelcontextprotocol/sdk/client/sse.js")));
            const transport = new SSEClientTransport(new URL(serverUrl));
            const client = new Client({ name: "atk-cli", version: "1.0.0" });
            try {
                await client.connect(transport);
                const result = await client.listTools();
                const tools = result.tools.map((tool) => ({
                    ...tool,
                    description: tool.description ?? "",
                }));
                return { requiresAuth: false, tools };
            }
            finally {
                await client.close();
            }
        }
        catch {
            // Both transports failed
            // Check if the original error was auth-related
            if (error?.message?.includes("401") ||
                error?.message?.includes("Unauthorized") ||
                error?.message?.includes("auth")) {
                return { requiresAuth: true, tools: [] };
            }
            // Return empty tools — caller should fall back to file input
            return { requiresAuth: false, tools: [] };
        }
    }
}
/**
 * Read and parse MCP tool definitions from a JSON file.
 * Accepts two formats:
 * - `{ tools: [{ name, description, inputSchema }] }` (mcp-tools.json format)
 * - `[{ name, description, inputSchema }]` (raw array of tools)
 *
 * @param filePath - Path to the JSON file containing tool definitions
 * @returns Array of MCPTool objects
 */
async function readMCPToolsFromFile(filePath) {
    if (!(await fs_extra_1.default.pathExists(filePath))) {
        throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileNotFound", filePath));
    }
    const content = await fs_extra_1.default.readJSON(filePath);
    let rawTools;
    if (Array.isArray(content)) {
        rawTools = content;
    }
    else if (content && Array.isArray(content.tools)) {
        rawTools = content.tools;
    }
    else {
        throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileInvalidFormat", '{ "tools": [...] }', filePath));
    }
    return rawTools.map((tool) => {
        if (!tool.name) {
            throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileMissingName", '"name"', filePath));
        }
        return {
            name: tool.name,
            description: tool.description ?? "",
            inputSchema: tool.inputSchema ?? tool.input_schema ?? { type: "object", properties: {} },
            outputSchema: tool.outputSchema ?? tool.output_schema,
            tags: tool.tags,
        };
    });
}
/**
 * Lightweight probe to check if an MCP server requires authentication.
 * Sends a GET request and checks for a 401 response + WWW-Authenticate header.
 * Does NOT attempt to fetch tools via MCP protocol.
 *
 * @param serverUrl - The URL of the remote MCP server
 * @returns Whether auth is required and the resource_metadata URL if available
 */
async function probeMCPServerAuth(serverUrl) {
    try {
        await axios_1.default.get(serverUrl, { timeout: 10000 });
        return { requiresAuth: false };
    }
    catch (error) {
        if (error?.response?.status === 401 || error?.status === 401) {
            const wwwAuth = error?.response?.headers?.["www-authenticate"];
            let authMetadataUrl;
            if (wwwAuth) {
                const match = wwwAuth.match(/resource_metadata=\s*"([^"]+)"/);
                if (match) {
                    authMetadataUrl = match[1];
                }
            }
            return { requiresAuth: true, authMetadataUrl };
        }
        return { requiresAuth: false };
    }
}
/**
 * Resolve OAuth metadata (authorization_endpoint, token_endpoint, refresh_endpoint) from an MCP server's
 * resource metadata URL. Follows the MCP auth flow:
 *   1. Fetch the resource metadata URL (from WWW-Authenticate header's resource_metadata)
 *   2. Extract authorization_servers[0]
 *   3. Construct the RFC 8414 well-known URL
 *   4. Fetch authorization_endpoint / token_endpoint from that well-known URL
 *
 * @param authMetadataUrl - The resource_metadata URL from the 401 WWW-Authenticate header
 * @param wellKnownUrl - Optional: if already known, skip discovery and use this URL directly
 * @returns MCPOAuthMetadata with authorization and token URLs
 */
async function resolveMCPOAuthMetadata(authMetadataUrl, wellKnownUrl) {
    let resolvedWellKnownUrl = wellKnownUrl;
    if (!resolvedWellKnownUrl) {
        if (!authMetadataUrl) {
            throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.mcpAuthMetadataUrlNotFound"));
        }
        const response = await axios_1.default.get(authMetadataUrl);
        if (response.status === 200 &&
            response.data &&
            response.data.authorization_servers &&
            response.data.authorization_servers.length > 0) {
            const mcpServerMetadataUrl = response.data.authorization_servers[0];
            // Transform to RFC 8414 well-known endpoint:
            // https://{domain}/.well-known/oauth-authorization-server{path}
            const serverUrl = new URL(mcpServerMetadataUrl);
            resolvedWellKnownUrl = `${serverUrl.protocol}//${serverUrl.host}/.well-known/oauth-authorization-server${serverUrl.pathname}`;
        }
        else {
            throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.mcpServerMetadataUrlNotFound"));
        }
    }
    const metadataResponse = await axios_1.default.get(resolvedWellKnownUrl);
    const authorizationUrl = metadataResponse.data?.authorization_endpoint;
    const tokenUrl = metadataResponse.data?.token_endpoint;
    const refreshUrl = metadataResponse.data?.refresh_endpoint;
    if (!authorizationUrl || !tokenUrl) {
        throw new Error((0, localizeUtils_1.getLocalizedString)("core.MCPForDA.authUrlNotFound"));
    }
    return { authorizationUrl, tokenUrl, refreshUrl };
}
//# sourceMappingURL=mcpToolFetcher.js.map